import { useCallback, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'

export interface CertificatesDateFilterState {
  startDate: Dayjs | null
  endDate: Dayjs | null
}

/**
 * Filter-state hook for the certificates page date-range filter.
 *
 * Uses explicit calendar dates (not relative presets). Both bounds are
 * optional — a null bound leaves that side open, and clearing both restores
 * the all-time view.
 */
export function useCertificatesDateFilter() {
  let [startDate, setStartDate] = useState<Dayjs | null>(null)
  let [endDate, setEndDate] = useState<Dayjs | null>(null)

  let updateStartDate = useCallback((value: Dayjs | null) => {
    setStartDate(value)
  }, [])

  let updateEndDate = useCallback((value: Dayjs | null) => {
    setEndDate(value)
  }, [])

  let clearFilter = useCallback(() => {
    setStartDate(null)
    setEndDate(null)
  }, [])

  // While the user types into a picker, MUI hands us a Dayjs that reports as
  // invalid (e.g. "07/" before the full date is entered) rather than null.
  // Treat those as "not yet a usable bound" so we never filter — or call
  // toISOString(), which throws RangeError — against an incomplete date.
  let validStartDate = useMemo(
    () => (startDate?.isValid() ? startDate : null),
    [startDate],
  )
  let validEndDate = useMemo(
    () => (endDate?.isValid() ? endDate : null),
    [endDate],
  )

  // The filter is "active" (and thus clearable via "All Time") whenever either
  // picker holds any input at all — including a partially-typed, still-invalid
  // date. Gating this on validity alone would leave the reset button disabled
  // while the user has an in-progress entry they want to clear.
  let hasActiveFilter = useMemo(
    () => startDate !== null || endDate !== null,
    [startDate, endDate],
  )

  // Invalid when both bounds are valid and the "to" date precedes the "from" date.
  let isInvalidRange = useMemo(
    () =>
      validStartDate !== null &&
      validEndDate !== null &&
      validEndDate.isBefore(validStartDate, 'day'),
    [validStartDate, validEndDate],
  )

  // Calendar-date strings ('YYYY-MM-DD') consumed by the client-side
  // aggregation utility. We deliberately emit the picked calendar date rather
  // than an absolute instant (toISOString): the picker gives us a local-midnight
  // Dayjs, and converting that to a UTC instant shifts the day backward in
  // negative-offset timezones, which would drop items whose completion date is
  // stored as UTC midnight (e.g. an uploaded external certificate). Only derived
  // from complete, valid dates, and suppressed while the range is invalid so we
  // never filter against a nonsensical span.
  let queryParams = useMemo(
    () => ({
      startDate:
        !isInvalidRange && validStartDate
          ? validStartDate.format('YYYY-MM-DD')
          : null,
      endDate:
        !isInvalidRange && validEndDate
          ? validEndDate.format('YYYY-MM-DD')
          : null,
    }),
    [validStartDate, validEndDate, isInvalidRange],
  )

  return {
    startDate,
    endDate,
    updateStartDate,
    updateEndDate,
    clearFilter,
    hasActiveFilter,
    isInvalidRange,
    queryParams,
    today: dayjs(),
  }
}
